import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V3 = '#51158C';
const V4 = '#2B0057';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter your email address');
    setLoading(true);
    try {
      const { data } = await API.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('Reset link sent! Check your email 📧');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px',
    background: 'var(--bg3)', border: '1.5px solid var(--border)', borderRadius: 10,
    color: 'var(--text)', fontSize: 15,
    fontFamily: "'Satoshi', 'DM Sans', sans-serif",
    transition: 'all 0.2s', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 16px 60px', background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
      {/* Background accent */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <div style={{ position: 'absolute', bottom: '-200px', right: '-200px', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${V4}44 0%, transparent 70%)` }} />
        <div style={{ position: 'absolute', top: '-200px', left: '-100px', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${V1}22 0%, transparent 70%)` }} />
      </div>

      <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 32, color: 'var(--v1)', marginBottom: 8 }}>
            Thiran<span style={{ color: 'var(--text)' }}>X</span>
          </div>
          <h2 style={{ fontSize: 'clamp(24px,5vw,32px)', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8, color: 'var(--text)' }}>
            Forgot Password?
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
            No worries! Enter your email and we'll send you a reset link
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="card" style={{
            background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: 32,
            boxShadow: 'var(--shadow-md)'
          }}>
            {/* Lock icon */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16, margin: '0 auto',
                background: `linear-gradient(135deg,${V4},${V3})`,
                border: `1px solid ${V1}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#B163FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
            </div>

            <div className="form-group">
              <label style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>Email Address</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = `${V1}77`}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            <button
              type="submit" disabled={loading}
              className="btn btn-primary"
              style={{
                width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px', marginTop: 12,
                fontWeight: 700, background: `linear-gradient(135deg,${V1},${V3})`,
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1, transition: 'all 0.2s'
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(127,0,255,0.3)'; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'none'; } }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--muted)', fontSize: 14 }}>
              Remember your password?{' '}
              <Link to="/login" style={{ color: 'var(--v1)', fontWeight: 700, textDecoration: 'none' }}>Back to Login</Link>
            </p>
          </form>
        ) : (
          /* Success state */
          <div className="card" style={{
            background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: 40,
            boxShadow: 'var(--shadow-md)', textAlign: 'center'
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', margin: '0 auto 24px',
              background: 'linear-gradient(135deg, #059669, #10b981)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h3 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Check Your Email</h3>
            <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.7, marginBottom: 8 }}>
              We've sent a password reset link to
            </p>
            <p style={{ color: 'var(--v1)', fontSize: 15, fontWeight: 700, marginBottom: 24 }}>{email}</p>
            <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.7, marginBottom: 32 }}>
              The link will expire in 1 hour. If you don't see the email, check your spam folder.
            </p>

            <button
              onClick={() => { setSent(false); setEmail(''); }}
              style={{
                background: 'none', border: `1.5px solid var(--border)`, borderRadius: 10,
                color: 'var(--text)', padding: '12px 24px', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.2s', marginRight: 12,
              }}
            >
              Try Another Email
            </button>
            <Link to="/login" style={{
              display: 'inline-block', color: 'var(--v1)', fontSize: 14, fontWeight: 700,
              textDecoration: 'none', padding: '12px 24px',
            }}>
              Back to Login
            </Link>
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          <Link to="/" style={{ color: 'var(--v1)', textDecoration: 'none', fontWeight: 600 }}>← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
